"use client";

import React from "react";
import Image from "next/image";

interface WhyAttendProps {
  whyAttendData: {
    text: string;
    iconUrl: string;
  }[];
  learnData: string[];
}

const WhyAttend: React.FC<WhyAttendProps> = ({
  whyAttendData = [],
  learnData = [],
}) => {
  const features = learnData.length > 0 ? learnData : [];
  const whyAttendBoxes =
    whyAttendData.length > 0
      ? whyAttendData.map((item) => ({
          icon: item.iconUrl,
          title: item.text,
        }))
      : [];

  return (
    <div className="w-full bg-[#fff]">
      <div className="main_container mx-auto md:px-5 md:py-12 py-4 flex flex-col gap-8 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-60 lg:gap-40 items-center w-full md:px-2">
          <div className="flex flex-col gap-4">
            <h2 className="text-[28px] md:text-[32px] lg:text-[36px] font-semibold tracking-[-1.76px] font-sans leading-[1.2] text-[#000000]">
              Why attend?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
              {whyAttendBoxes?.map((box, index) => (
                <div
                  key={index}
                  className="flex flex-row items-start md:gap-4 gap-3 px-3 py-3 md:px-4 md:py-4 rounded-[12px] bg-[#F3F6FA] border border-[rgba(0,0,0,0.06)] min-h-[72px] md:min-h-[100px]"
                >
                  <div className="md:w-10 md:h-10 w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-[20px] bg-[#061A2F]">
                    <Image
                      src={box.icon}
                      alt={box.title}
                      width={24}
                      height={24}
                      className="md:w-5 md:h-5 w-4 h-4 object-contain"
                    />
                  </div>

                  <p className="text-[13px] md:text-[15px] font-medium text-left font-sans leading-tight break-words flex-1 min-w-0 text-[#000000]">
                    {box.title}
                  </p>
                </div>
              ))}
            </div>
            {whyAttendBoxes.length === 0 && (
              <p className="text-[#A8A8A8] text-sm">
                No &quot;Why attend&quot; data available.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-[28px] md:text-[32px] lg:text-[36px] font-semibold tracking-[-1.76px] font-sans leading-[1.2] text-[#000000]">
              What will you learn?
            </p>
            {features?.map((feature, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 pt-1 pb-4 ${
                  index !== features.length - 1
                    ? "border-b-[0.5px] border-[#000]/15"
                    : ""
                }`}
              >
                <div className="flex-shrink-0 w-7 h-7">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-label="check"
                  >
                    <path
                      d="M9.49703 5.37797C9.53771 5.4186 9.56998 5.46685 9.592 5.51996C9.61401 5.57307 9.62535 5.63001 9.62535 5.6875C9.62535 5.74499 9.61401 5.80192 9.592 5.85504C9.56998 5.90815 9.53771 5.9564 9.49703 5.99703L6.43453 9.05953C6.3939 9.10021 6.34565 9.13248 6.29254 9.15449C6.23943 9.17651 6.1825 9.18784 6.125 9.18784C6.06751 9.18784 6.01058 9.17651 5.95747 9.15449C5.90435 9.13248 5.8561 9.10021 5.81547 9.05953L4.50297 7.74703C4.42088 7.66494 4.37476 7.5536 4.37476 7.4375C4.37476 7.3214 4.42088 7.21006 4.50297 7.12797C4.58506 7.04588 4.69641 6.99976 4.8125 6.99976C4.9286 6.99976 5.03994 7.04588 5.12203 7.12797L6.125 8.13148L8.87797 5.37797C8.9186 5.33729 8.96685 5.30502 9.01997 5.283C9.07308 5.26099 9.13001 5.24966 9.1875 5.24966C9.245 5.24966 9.30193 5.26099 9.35504 5.283C9.40815 5.30502 9.4564 5.33729 9.49703 5.37797ZM12.6875 7C12.6875 8.12488 12.3539 9.2245 11.729 10.1598C11.104 11.0951 10.2158 11.8241 9.17651 12.2546C8.13726 12.685 6.99369 12.7977 5.89043 12.5782C4.78716 12.3588 3.77374 11.8171 2.97833 11.0217C2.18292 10.2263 1.64124 9.21284 1.42179 8.10958C1.20233 7.00631 1.31496 5.86274 1.74544 4.82349C2.17591 3.78423 2.90489 2.89597 3.8402 2.27102C4.7755 1.64607 5.87512 1.3125 7 1.3125C8.50793 1.31409 9.95364 1.91382 11.0199 2.98009C12.0862 4.04636 12.6859 5.49207 12.6875 7ZM11.8125 7C11.8125 6.04818 11.5303 5.11773 11.0014 4.32632C10.4726 3.53491 9.72104 2.91808 8.84167 2.55383C7.9623 2.18958 6.99466 2.09428 6.06113 2.27997C5.1276 2.46566 4.27009 2.92401 3.59705 3.59705C2.92401 4.27009 2.46566 5.12759 2.27997 6.06113C2.09428 6.99466 2.18959 7.96229 2.55383 8.84166C2.91808 9.72103 3.53491 10.4726 4.32632 11.0014C5.11773 11.5303 6.04818 11.8125 7 11.8125C8.27591 11.8111 9.49915 11.3036 10.4014 10.4014C11.3036 9.49915 11.8111 8.27591 11.8125 7Z"
                      fill="#061A2F"
                    />
                  </svg>
                </div>
                <p className="text-sm md:text-base font-medium leading-relaxed text-[#4B5563]">
                  {feature}
                </p>
              </div>
            ))}
            {features.length === 0 && (
              <p className="text-[#A8A8A8] text-sm">
                No learning points available.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyAttend;
